import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { validatePhoneNumberLength } from 'libphonenumber-js'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { StorageKeys } from '~/atoms/StorageKeys'
import { MangoButton } from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { FaxAttachmentConfigMultifile } from '~/constants/fileAttachmentConfig'
import { useAnalytics } from '~/hooks/useAnalytics'
import { NewFaxForm } from '~/pages/Home/Modals/NewFaxModal/NewFaxForm'
import { NewFaxLoadingView } from '~/pages/Home/Modals/NewFaxModal/NewFaxLoadingView'
import { useNewFaxViewModel } from '~/pages/Home/Modals/NewFaxModal/viewModels/NewFaxViewModel'
import { AnalyticsNamespace } from '~/types/Analytics'
import { FaxAnalyticsEvents } from '~/types/Analytics/FaxAnalyticsEvents'
import { formatPhoneNumber, getValidNumber } from '~/utils/formatter'

import { PhoneNumber } from '@mangovoice/types'

import { useFaxCoverPage } from './FaxCoverPage'
import {
  cacheEmailConfirmationsDefault,
  FaxSendingStep,
  NewFax,
} from './viewModels'

const persistedEmailConfirmationsAtom =
  atomWithStorage<NewFax.CacheEmailConfirmations>(
    StorageKeys.FaxEmailConfirmations,
    cacheEmailConfirmationsDefault,
  )

export const NewFaxModal = () => {
  const {
    faxModal: [faxModalState, setFaxModalState],
  } = useModalState()
  const analytics = useAnalytics(AnalyticsNamespace.FAXING)
  const [persistedEmailConfirmations, setPersistedEmailConfirmations] = useAtom(
    persistedEmailConfirmationsAtom,
  )
  const { t } = useTranslation()
  const { state, actions, mutators } = useNewFaxViewModel()
  const {
    includeCoverPage,
    setIncludeCoverPage,
    coverPageFile,
    handleSetCoverPageCount,
    setCoverPageNote,
    coverPageNote,
  } = useFaxCoverPage({
    toNumber: state.toNumber,
    fromNumber: state.selectedPhoneNumber?.number ?? '',
  })

  const isToNumberInvalid = validatePhoneNumberLength(state.toNumber, {
    defaultCountry: 'US',
  })
  const sendReady =
    state.faxSendingStep === FaxSendingStep.NotStarted &&
    state.fromNumber &&
    !isToNumberInvalid &&
    state.selectedFiles.length &&
    state.selectedFiles.reduce((total, file) => total + file.size, 0) <=
      FaxAttachmentConfigMultifile.MaximumFileSize

  const showLoader = state.faxSendingStep >= FaxSendingStep.Sending

  const handleFromSelected = (from: PhoneNumber | null) => {
    mutators.selectedPhoneNumber = from
  }

  // initial load actions, reset state on close
  useEffect(() => {
    if (!faxModalState.open) {
      return
    }

    mutators.sendEmailConfirmation = persistedEmailConfirmations.enabled
    mutators.emailConfirmations = persistedEmailConfirmations.emails

    if (state.lastUsedFaxNumber && !state.selectedPhoneNumber) {
      mutators.selectedPhoneNumber = state.lastUsedFaxNumber
    }
    if (faxModalState.number) {
      mutators.toNumber = formatPhoneNumber(faxModalState.number)
    }
  }, [faxModalState.open])

  // attempts to load fax data if the modal is open and there is no list data
  useEffect(() => {
    if (!faxModalState.open) {
      return
    }

    mutators.resendId = faxModalState.resendId
  }, [faxModalState.open])

  const handleClosed = () => {
    actions.resetState()
    setFaxModalState({ ...faxModalState, open: false, resendId: 0 })
  }

  const handleToChanged = (to: string) => {
    const phoneNumber = formatPhoneNumber(to)
    mutators.toNumber = phoneNumber
  }

  const trackFileSelectionChanged = (files: File[]) => {
    if (!state.selectedFiles.length) {
      return
    }

    analytics.track(
      files.length
        ? FaxAnalyticsEvents.FileSelected
        : FaxAnalyticsEvents.FileRemoved,
    )
  }

  const handleFileSelectionChanged = (files: File[]) => {
    trackFileSelectionChanged(files)
    handleSetCoverPageCount(files)
    mutators.selectedFiles = files
  }

  const handleEmailConfirmationToggled = (enabled: boolean) => {
    mutators.sendEmailConfirmation = enabled
    setPersistedEmailConfirmations({
      ...persistedEmailConfirmations,
      enabled,
    })
  }

  const trackEmailConfirmationsChanged = (newEmails: string[]) => {
    if (newEmails.length > state.emailConfirmations.length) {
      analytics.track(FaxAnalyticsEvents.ConfirmationEmailAdded)
    } else if (newEmails.length < state.emailConfirmations.length) {
      analytics.track(FaxAnalyticsEvents.ConfirmationEmailRemoved)
    }
  }

  const handleEmailConfirmationsChanged = (emails: string[]) => {
    trackEmailConfirmationsChanged(emails)

    mutators.emailConfirmations = emails
    setPersistedEmailConfirmations({
      ...persistedEmailConfirmations,
      emails,
    })
  }

  const handleSendFax = () => {
    if (!state.fromNumber || !state.toNumber || !state.selectedFiles) {
      return
    }
    const files = [...state.selectedFiles]
    if (coverPageFile) {
      files.unshift(coverPageFile)
    }
    actions.sendFax(
      getValidNumber(state.fromNumber),
      getValidNumber(state.toNumber),
      files,
      state.sendEmailConfirmation ? state.emailConfirmations : [],
    )
  }

  const trackFaxSendingStep = (step: FaxSendingStep) => {
    switch (step) {
      case FaxSendingStep.Success:
        analytics.track(FaxAnalyticsEvents.SendSuccess)
        break
      case FaxSendingStep.Failure:
        analytics.track(FaxAnalyticsEvents.SendFailure)
        break
    }
  }

  // watches fax sending step results and issues analytics events
  useEffect(() => {
    trackFaxSendingStep(state.faxSendingStep)
  }, [state.faxSendingStep])

  const modalContent = useMemo(() => {
    if (!faxModalState.open) {
      return
    }

    //default state
    if (!showLoader) {
      return (
        <NewFaxForm
          selectedFrom={state.selectedPhoneNumber}
          to={state.toNumber}
          coverPageNote={coverPageNote}
          setCoverPageNote={setCoverPageNote}
          includeCoverPage={includeCoverPage}
          setIncludeCoverPage={setIncludeCoverPage}
          coverPageFile={coverPageFile}
          isToInvalid={Boolean(isToNumberInvalid)}
          sendEmailConfirmation={state.sendEmailConfirmation}
          emailConfirmations={state.emailConfirmations}
          onFromSelected={handleFromSelected}
          onToChanged={handleToChanged}
          onFileSelectionChanged={handleFileSelectionChanged}
          onEmailConfirmationToggled={handleEmailConfirmationToggled}
          onEmailConfirmationsChanged={handleEmailConfirmationsChanged}
        />
      )
    } else {
      return <NewFaxLoadingView progress={state.faxSendingProgress} />
    }
  }, [state])

  // watches for fax send success or error and closes the modal
  useEffect(() => {
    if (state.faxSendingStep < FaxSendingStep.Success) {
      return
    }

    handleClosed()
  }, [state.faxSendingStep])

  return (
    <MangoModal
      isOpen={faxModalState.open}
      closeButton={!showLoader}
      onClose={showLoader ? undefined : handleClosed}
      header={t('newFaxModal.header')}
      footer={
        !showLoader && (
          <MangoButton disabled={!sendReady} onClick={handleSendFax}>
            {t('newFaxModal.footer.sendButtonText')}
          </MangoButton>
        )
      }
    >
      {modalContent}
    </MangoModal>
  )
}
